
import { useTranslation } from "react-i18next"
import { format } from "date-fns"
import { X, Edit, Trash } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"

export function AppointmentDetails({
  appointment,
  onClose,
}: {
  appointment: any
  onClose: () => void
}) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle>{appointment.title}</CardTitle>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-muted-foreground">{format(appointment.date, "EEEE, MMMM d, yyyy")}</div>
          <Badge
            variant={appointment.status === "confirmed" ? "default" : "secondary"}
            className={appointment.status === "confirmed" ? "bg-green-500" : "bg-yellow-500"}
          >
            {appointment.status === "confirmed" ? t("appointments.confirmed") : t("appointments.pending")}
          </Badge>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="font-medium">{t("appointments.time")}:</span>
            <span>
              {format(appointment.date, "HH:mm")} - {format(appointment.endDate, "HH:mm")}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium">{t("appointments.duration")}:</span>
            <span>
              {Math.round((appointment.endDate.getTime() - appointment.date.getTime()) / (1000 * 60))}{" "}
              {t("appointments.minutes")}
            </span>
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="font-medium">{t("appointments.client")}:</span>
            <span>{appointment.clientName}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium">{t("appointments.trainer")}:</span>
            <span>{appointment.trainerName}</span>
          </div>
        </div>

        <Separator />

        <div>
          <h4 className="font-medium mb-1">{t("appointments.notes")}:</h4>
          <p className="text-sm">{appointment.notes || t("appointments.noNotes")}</p>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end space-x-2">
        <Button variant="outline" size="sm" className="flex items-center">
          <Edit className="mr-2 h-4 w-4" />
          {t("common.edit")}
        </Button>
        <Button variant="destructive" size="sm" className="flex items-center">
          <Trash className="mr-2 h-4 w-4" />
          {t("common.delete")}
        </Button>
      </CardFooter>
    </Card>
  )
}
